// List of functions and components for the Dashboards page (client and publisher dashboards)

import { ReactComponent as FolderIcon } from '../icons/folder.svg';
import { ReactComponent as SendIcon } from '../icons/send.svg';
import { useState, useEffect } from 'react';
import { useData } from './DataProvider';
import { Loader, APIloader } from '../Commons';
import { Chart } from 'react-google-charts';
import { useAlert } from './Alert';
import { useFetcher } from 'react-router-dom';

// Utility to format numbers
const numbers = new Intl.NumberFormat();

// Date range options for the dashboard
const thisYear = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;
const prevMonth = (thisMonth === 1) ? 12 : thisMonth - 1;
export const dateRangeOptions = [
    ['daily', 7, '7 ימים אחרונים'],
    ['daily', 14, '14 ימים אחרונים'],
    ['daily', 28, '28 ימים אחרונים'],
    ['monthly', thisMonth, 'החודש'],
    ['monthly', prevMonth, 'חודש שעבר'],
    ['yearly', thisYear, 'השנה'],
    ['yearly', thisYear-1, 'שנה שעברה'],
    ['yearly', thisYear-2, thisYear-2]
];

export const DateSelect = ({range, setRange}) => {
  
  // TODO: consider useMemo to avoid unnecessary re-renders

  return (
    <div className="group cursor-pointer z-10 rounded bg-white drop-shadow hover:bg-primary hover:text-white transition-all duration-200">
      <div className="flex items-center gap-2 px-4 py-4">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke='currentColor' className="h-6 w-6">
          <path d="M3 9H21M7 3V5M17 3V5M6 12H8M11 12H13M16 12H18M6 15H8M11 15H13M16 15H18M6 18H8M11 18H13M16 18H18M6.2 21H17.8C18.9201 21 19.4802 21 19.908 20.782C20.2843 20.5903 20.5903 20.2843 20.782 19.908C21 19.4802 21 18.9201 21 17.8V8.2C21 7.07989 21 6.51984 20.782 6.09202C20.5903 5.71569 20.2843 5.40973 19.908 5.21799C19.4802 5 18.9201 5 17.8 5H6.2C5.0799 5 4.51984 5 4.09202 5.21799C3.71569 5.40973 3.40973 5.71569 3.21799 6.09202C3 6.51984 3 7.07989 3 8.2V17.8C3 18.9201 3 19.4802 3.21799 19.908C3.40973 20.2843 3.71569 20.5903 4.09202 20.782C4.51984 21 5.07989 21 6.2 21Z" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <button className="text-black group-hover:text-white">{dateRangeOptions[range.key][2]}</button>
      </div>
      <div className="min-w-32 transition-all duration-500 absolute text-nowrap w-full flex flex-col bg-white mt-1 px-4 text-gray-800 rounded shadow-xl opacity-0 scale-y-50 origin-top group-hover:opacity-100 group-hover:scale-y-100">
        {dateRangeOptions.map((option, index) => (
          <button key={index} className="block border-b border-gray-100 py-1 text-gray-500 hover:text-primary" onClick={() => setRange({period: option[0], value: option[1], key: index})}>{option[2]}</button>
        ))}
      </div>
    </div>
  );
}

export const ActiveCampaigns = ({formatter, active, total}) => {
  return (
  <section className="px-2 sm:px-4 group">
    <div className='flex gap-3 flex-col sm:flex-row'>
      <div className='bg-primary group-hover:bg-violet-600 my-auto p-3 rounded-xl flex gap-2 text-white'>
        <FolderIcon className='group-hover:-rotate-45' />
        <span className='sm:hidden'>קמפיינים פעילים</span>
      </div>
      <div className='px-2 sm:px-0'>
        <p className="hidden text-md sm:block">קמפיינים פעילים</p>
        <p className="text-2xl md:text-3xl group-hover:text-violet-600 group-hover:font-bold">{formatter.format(active)}</p>
        {total && <p className='text-gray-500 text-sm sm:text-xs'>מתוך {total} קמפיינים</p> }
      </div>
    </div>
  </section>
  );
}

export const ClicksChart = ({formatter, dateRange, setSumPeriodClicks}) => {
  const data = useData();
  const [sumClicks, setSumClicks] = useState(0);

  useEffect(() => {
    if(data) {
      const clicks = data.reduce((acc, row) => acc + row.clicks, 0);
      setSumClicks(clicks);
      setSumPeriodClicks(clicks);
    }
  }, [data, setSumPeriodClicks]);

  if(!data)
    return <Loader />;

  const currValue = data[data.length - 1];
  const prevValue = data[data.length - 2];
  let prct = null;
  let sign = null;
  let period = dateRange.period === 'yearly' ? 'חודש שעבר' : 'אתמול';

  if(currValue && prevValue) {
    prct = (currValue.clicks / prevValue.clicks * 100) - 100;
    sign = (prct >= 0);
    prct = Math.abs(prct);
  }

  return (
    <section className="px-2 sm:px-4 group">
      <div className='flex flex-col gap-3 sm:flex-row'>
        <div className='bg-primary group-hover:bg-violet-600 my-auto p-3 rounded-xl flex gap-2 text-white'>
          <SendIcon className='rotate-[135deg] group-hover:rotate-[90deg]' />
          <span className='sm:hidden'>קליקים</span>
        </div>
        <div className='px-2 sm:px-0'>
          <p className='hidden text-md sm:block'>קליקים</p>
          <p className="text-2xl md:text-3xl group-hover:text-violet-600 group-hover:font-bold">{formatter.format(sumClicks)}</p>
          {prct ? (
            <div className={`mt-1 flex gap-1 ${sign ? 'text-green-600' : 'text-red-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="size-4 mt-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {sign ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/>
                )}
              </svg>
              <p className="flex gap-2 text-sm sm:text-xs">
                <span className="font-medium">{formatter.format(prct)}%</span>
                <span className="text-gray-500">{period}</span>
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export const PerformanceChart = ({dateRange}) => {
  const data = useData();

  if(!data || data.errors)
    return <Loader />;

  const columns = (dateRange.period === 'daily') ? dateRange.value : 12; // fix number of columns to date range
  const tableData = data.map((row, index) => {
    return [row.value, row.clicks];
  });

  while(tableData.length < columns)
    tableData.unshift(['', 0]);

  // add headers
  tableData.unshift(['חודש', 'קליקים']);

  const options = {
    chart: {
      title: 'קליקים',
      subtitle: 'קליקים לפי תאריך',
      chatArea: { 
        width: '100%',
        height: '100%',
      }
    },
    colors: ['#67c6e3', '#5356ff'],
    backgroundColor: 'transparent',
    legend: { position: "none" } // hide legend
  };

  return <Chart chartType='ColumnChart' className='w-full' data={tableData} options={options} width='100%' height='100%' />;
}

export const ContactUS = ({campaign, setCampaign}) => {
  const fetcher = useFetcher();
  const { showAlert } = useAlert();
  const isSubmitting = (fetcher.state !== 'idle');

  useEffect(() => {
    if(fetcher.state === 'idle' && fetcher.data && fetcher.data.messages) {
      setCampaign(false);
      fetcher.data.messages.map((message) => showAlert(message, 'success'));
    }
  }, [fetcher.state, fetcher.data, setCampaign]); // do not include showAlert in dependencies, it will cause infinite loop

  if(!campaign)
    return null;

  return (
    <div className='fixed h-full w-full bg-gray-900 bg-opacity-60 z-50 inset-0'>
      <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-6 lg:p-6 relative w-fit mx-auto top-1/4 lg:top-1/3" role="alert">
        <h2 className='font-bold text-3xl text-center'>פנייה לניהול</h2>
        <p className="mt-4 text-gray-500 text-sm text-center">פנייה לניהול קליקה לגבי קמפיין <span className='font-bold'>{campaign.name}</span></p>
        <fetcher.Form method="post" action='/contactUS' className="mt-6 flex flex-col gap-4">
          <div>
            <label htmlFor="title" className="sr-only">כותרת הפנייה</label>
            <label htmlFor="title" className='text-sm leading-7'>כותרת הפנייה</label>
            <input type="text" id="title" name="title" className="w-full rounded-lg border-2 border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-primary" required placeholder="כותרת הפנייה" />
          </div>
          <div>
            <label htmlFor="message" className="sr-only">תוכן הפנייה</label>
            <label htmlFor="message" className='text-sm leading-7'>תוכן הפנייה</label>
            <textarea id="message" name="message" className="w-full rounded-lg border-2 border-gray-300 px-2 py-2 text-sm h-40 focus:outline-none focus:border-primary" required placeholder="תוכן הפנייה"></textarea>
          </div>
          {fetcher.data && fetcher.data.errors ? (
          <div className='text-red-500 text-sm'>
            {fetcher.data.errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
          ) : null}
          <div className='flex flex-row gap-4'>
            {campaign.setId && <input type="hidden" name="setId" value={campaign.setId} />}
            {campaign.id && <input type="hidden" name="campaignId" value={campaign.id} />}
            <button name="cancel" type="button" className="bg-white text-primary border border-primary py-2 transition delay-100 grow rounded-lg text-center text-sm font-semibold hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed" onClick={() => setCampaign(false)} disabled={isSubmitting}>בעצם לא</button>
            <button name="submit" type="submit" className="bg-primary text-white border border-primary py-2 transition delay-100 grow rounded-lg text-center text-sm font-semibold hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed" disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="h-4 w-4 ml-2 rounded-full animate-spin border-2 border-t-white border-gray-400 shadow-md"></div>
                  <span>שולח...</span>
                </div>
              ) : (
                'שליחה'
              )}
            </button>
          </div>
        </fetcher.Form>
      </div>
    </div>
  );
}

export const ChangeCampaignStatus = ({campaign, display, setCampaigns}) => {
  const [isSubmitting, setSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const changeSetStatus = (newStatus) => {
    setSubmitting(true);
    const iStatus = (newStatus) ? 1 : 0;
    const userType = localStorage.getItem('userType');
    const actionPath = (userType === 'publisher') ? '/publisher/adSetStatus' : '/client/campaignStatus';
    const body = new URLSearchParams({status: iStatus});
    
    if(campaign.setId)
      body.append('adSetId', campaign.setId);
    
    if(campaign.id)
      body.append('campaignId', campaign.id);

    const request = new Request(actionPath, { method: 'POST', body: body });
    APIloader(request).then(res => {
      setSubmitting(false);
      if(!res || res.errors) {
        showAlert(res.errors[0], 'error');
      } else {
        showAlert(res.messages[0], 'success');
        setCampaigns((prev) => prev.map((data) => {
          if(campaign?.setId && data.setId === campaign.setId) // publisher, update ad set status
            return {...data, setActive: newStatus};
          else if(campaign?.id && data.id === campaign.id) // client, update campaign status
            return {...data, active: newStatus};

          return data; // no match, return original data
        }));
      }
      display(false);
    });
  }

  if(!campaign)
    return null;

  return (
    <div className='fixed h-full w-full bg-gray-900 bg-opacity-60 z-50 inset-0'>
      <div className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-6 lg:p-6 relative w-fit mx-auto top-1/4 lg:top-1/3" role="alert">
        <h2 className='font-bold text-3xl text-center'>השעיית קמפיין</h2>
        <p className="m-4 text-gray-500 text-sm text-center">האם אתה בטוח שברצונך להשעות את קמפיין <span className='font-bold'>{campaign.name}</span>?</p>
        <div className='flex flex-row gap-4'>
          <button onClick={() => display(false)} name="cancel" type="button" className="bg-white text-primary border border-primary py-2 transition delay-100 grow rounded-lg text-center text-sm font-semibold hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed" disabled={isSubmitting}>בעצם לא</button>
          <button onClick={() => changeSetStatus(!campaign.setActive)} name="submit" type="submit" className="bg-primary text-white border border-primary py-2 transition delay-100 grow rounded-lg text-center text-sm font-semibold hover:bg-blue-500 hover:text-white hover:border-blue-500 disabled:opacity-75 disabled:cursor-not-allowed" disabled={isSubmitting}>
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="h-4 w-4 ml-2 rounded-full animate-spin border-2 border-t-white border-gray-400 shadow-md"></div>
                <span>משעה קמפיין...</span>
              </div>
            ) : (
              'השעיה'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}